import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { memo, useCallback, useMemo } from "react";
import { Account } from "@common/types/domains/accounting/account";
import { AccountingLedgerBasis } from "@common/types/domains/accounting/ledger";
import { displayAccountLabel } from "@common/helpers/accounting";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { Picker } from "@design-system/Picker/Picker.lib";
import { useViewTableMeta } from "@hooks/view/useViewTableMeta";
import { AccountCombobox } from "@components/accounts/AccountCombobox";
import { AllocationTableOptions } from "../editAllocation/AllocationTable";
import { resolveRemainingRowLedger } from "../editAllocation/remainingRowLedger";
import { AccrualMatchTableCallbacks, MatchableItem } from "../types";

type RemainingLedgerAccountCellProps = CellContext<
    ViewTableFeatures,
    MatchableItem,
    unknown
> & {
    ledgerBasis: AccountingLedgerBasis;
    accountId: string | undefined;
    onAccountChange:
        | ((id: string, accountId: string | null) => void)
        | undefined;
};

export const RemainingLedgerAccountCell = memo(
    ({
        row,
        table,
        ledgerBasis,
        accountId,
        onAccountChange,
    }: RemainingLedgerAccountCellProps) => {
        const meta = useViewTableMeta<
            AccrualMatchTableCallbacks,
            AllocationTableOptions
        >(table);
        const { entityId, accounts } = resolveRemainingRowLedger({
            options: meta.options,
            item: row.original,
        });
        const showAccountNumbers = meta.options?.showAccountNumbers ?? false;
        const selectedAccount = useMemo(
            () => accounts?.find((account) => account.id === accountId),
            [accounts, accountId],
        );
        const handleChange = useCallback(
            (account: Account | null) => {
                onAccountChange?.(row.original.id, account?.id ?? null);
            },
            [onAccountChange, row.original.id],
        );
        const label = selectedAccount
            ? displayAccountLabel(selectedAccount, showAccountNumbers)
            : "";

        return (
            <ViewTableCell captureClicks>
                <AccountCombobox
                    showAccountCode={showAccountNumbers}
                    selectedAccount={selectedAccount}
                    onChange={handleChange}
                    entityId={entityId}
                    ledgerBasis={ledgerBasis}
                    trigger={<Picker.CellContent value={label || undefined} />}
                />
            </ViewTableCell>
        );
    },
);
RemainingLedgerAccountCell.displayName = "RemainingLedgerAccountCell";
